'use client';

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { CSSProperties, RefObject } from 'react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { dispatchClosePublicMobileNav } from '../../../lib/publicMobileNavEvents';
import { Button } from '../../../components/ui/Button';
import { IconButton } from '../../../components/ui/icon-button';
import { cn } from '../../../lib/utils';
import type { RankedAnimal } from '../types';
import { MatchShareEmailDialog } from './MatchShareEmailDialog';
import { MatchShareQuizResultsListSlide } from './MatchShareQuizResultsListSlide';
import { MatchShareTopThreeSlide } from './MatchShareTopThreeSlide';
import type { ShareOverlayPalette } from './ShareToSocialsPanel';

const SHARE_OVERLAY_PALETTE_STANDALONE: ShareOverlayPalette = 'figmaDark';

const SLIDE_COUNT = 2;

export type MatchShareResultsFlowProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Restore focus here when the flow closes (e.g. Share results button). */
  restoreFocusRef: RefObject<HTMLElement | null>;
  results: RankedAnimal[];
  /** Public `GET /match/:id` URL for this run (for future share handlers / email). */
  publicShareUrl: string;
  /** Persisted ID from `POST /match` — required for email share API. */
  shareMatchResultsId?: string | null;
  /** Hides Previous / Next and the visible “Slide i of N” line (slide dots stay). */
  hideManualSlideControls?: boolean;
  /**
   * When the quiz runs under `AdoptPublicQuizThemeRoot`, pass the same `--theme-*` map so
   * this portaled overlay picks up shelter tokens (`data-app-theme` + bridge in `globals.css`).
   */
  portalShelterThemeVars?: Record<string, string> | null;
  /**
   * When > 0, advances to the next slide on this interval in a loop while open.
   * The active slide dot shows a left-to-right progress fill over the same duration.
   * Disabled when `prefers-reduced-motion: reduce` is set.
   */
  autoAdvanceMs?: number;
  className?: string;
};

export function MatchShareResultsFlow({
  isOpen,
  onClose,
  restoreFocusRef,
  results,
  publicShareUrl,
  shareMatchResultsId = null,
  hideManualSlideControls = false,
  autoAdvanceMs,
  className,
  portalShelterThemeVars = null,
}: MatchShareResultsFlowProps) {
  const titleId = useId();
  const descriptionId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const advanceRafRef = useRef(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [activeSlideProgress, setActiveSlideProgress] = useState(0);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  /** `null` until mounted — avoids SSR/client mismatch on `prefers-reduced-motion`. */
  const [motionOk, setMotionOk] = useState<boolean | null>(null);

  const emailShareDisabled = !shareMatchResultsId?.trim();

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setMotionOk(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const autoAdvanceActive = Boolean(
    isOpen && autoAdvanceMs && autoAdvanceMs > 0 && motionOk === true,
  );

  const shareHandlers = useMemo(
    () => ({
      onShareEmail: () => {
        if (!shareMatchResultsId?.trim()) return;
        setEmailDialogOpen(true);
      },
      onShareFacebook: () => {
        // Future: native share / deep link
      },
      onShareInstagram: () => {
        // Future
      },
      onShareTiktok: () => {
        // Future
      },
    }),
    [shareMatchResultsId],
  );

  const sortedMatches = useMemo(
    () => [...results].sort((a, b) => b.matchPercent - a.matchPercent),
    [results],
  );

  const topThree = useMemo(
    () => sortedMatches.slice(0, 3),
    [sortedMatches],
  );

  const slideTitle =
    slideIndex === 0
      ? topThree.length === 1
        ? 'Your top match'
        : 'Top 3 matches'
      : 'Share quiz results';

  useEffect(() => {
    if (!isOpen) return;
    setSlideIndex(0);
    setActiveSlideProgress(0);
    dispatchClosePublicMobileNav();
  }, [isOpen]);

  /** Share overlay body scrolls independently — always start at top of slide content. */
  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = bodyScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [isOpen, slideIndex]);

  useEffect(() => {
    if (!isOpen) setEmailDialogOpen(false);
  }, [isOpen]);

  useEffect(() => {
    cancelAnimationFrame(advanceRafRef.current);
    if (!isOpen || !autoAdvanceMs || autoAdvanceMs <= 0 || motionOk !== true) {
      setActiveSlideProgress(0);
      return;
    }

    setActiveSlideProgress(0);
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / autoAdvanceMs);
      setActiveSlideProgress(t);
      if (elapsed >= autoAdvanceMs) {
        setSlideIndex((i) => (i + 1) % SLIDE_COUNT);
        return;
      }
      advanceRafRef.current = requestAnimationFrame(tick);
    };
    advanceRafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(advanceRafRef.current);
  }, [isOpen, slideIndex, autoAdvanceMs, motionOk]);

  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      closeRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  /** Stop the page behind the portal from scrolling on mobile (especially iOS). */
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleClose = useCallback(() => {
    onClose();
    window.setTimeout(() => {
      restoreFocusRef.current?.focus();
    }, 0);
  }, [onClose, restoreFocusRef]);

  const goPrev = useCallback(() => {
    setSlideIndex((i) => (i - 1 + SLIDE_COUNT) % SLIDE_COUNT);
  }, []);

  const goNext = useCallback(() => {
    setSlideIndex((i) => (i + 1) % SLIDE_COUNT);
  }, []);

  if (!isOpen) return null;

  const sharePalette: ShareOverlayPalette = portalShelterThemeVars
    ? 'default'
    : SHARE_OVERLAY_PALETTE_STANDALONE;

  const overlay = (
    <div
      className={cn(
        /* Hard viewport height fixes iOS flex + scroll: body must not overlap header */
        'fixed inset-0 z-100 flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden bg-background text-foreground backdrop-blur-sm motion-reduce:transition-none',
        'pt-[env(safe-area-inset-top)] pb-[max(1rem,env(safe-area-inset-bottom))]',
        className,
      )}
      data-app-theme={portalShelterThemeVars ? 'adopt-quiz' : undefined}
      style={
        portalShelterThemeVars
          ? (portalShelterThemeVars as CSSProperties)
          : undefined
      }
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <p id={descriptionId} className='sr-only'>
        Share your quiz match results. Slide {slideIndex + 1} of {SLIDE_COUNT}.
        {autoAdvanceActive
          ? (() => {
              const sec = (autoAdvanceMs ?? 0) / 1000;
              const label = Number.isInteger(sec) ? String(sec) : sec.toFixed(1);
              return ` Slides advance automatically every ${label} seconds.`;
            })()
          : hideManualSlideControls
            ? ' Use the slide indicators to change slides.'
            : ' Use previous and next buttons to change slides.'}{' '}
        Press Escape to close. Public results link: {publicShareUrl}
      </p>

      <div className='relative z-10 flex shrink-0 flex-col gap-3 border-b border-border bg-muted/40 px-4 py-4 sm:px-6'>
        <div className='flex items-start justify-between gap-3'>
          <h2
            id={titleId}
            className='min-w-0 flex-1 pr-2 font-heading text-lg font-bold tracking-tight text-foreground sm:text-xl'
          >
            {slideTitle}
          </h2>
          <IconButton
            ref={closeRef}
            type='button'
            variant='ghost'
            className='shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground [&_svg]:size-6'
            aria-label='Close share view'
            onClick={handleClose}
          >
            <XMarkIcon className='size-6' aria-hidden />
          </IconButton>
        </div>

        <div
          className='flex gap-2'
          role='group'
          aria-label='Share slides'
        >
          {Array.from({ length: SLIDE_COUNT }, (_, i) => {
            const isActive = slideIndex === i;
            const showProgressFill =
              isActive &&
              Boolean(autoAdvanceMs && autoAdvanceMs > 0) &&
              motionOk === true;

            return (
              <Button
                key={i}
                type='button'
                variant='ghost'
                className={cn(
                  'relative h-2 min-h-2 min-w-0 flex-1 overflow-hidden rounded-full border-0 p-0 shadow-none sm:h-1.5',
                  'hover:bg-accent/50',
                  isActive && !showProgressFill && 'bg-primary-600 hover:bg-primary-600',
                  (!isActive || showProgressFill) && 'bg-muted',
                )}
                aria-label={`Show slide ${i + 1}`}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => setSlideIndex(i)}
              >
                {showProgressFill ? (
                  <span
                    className='absolute inset-y-0 left-0 rounded-full bg-primary-600 motion-reduce:transition-none'
                    style={{ width: `${activeSlideProgress * 100}%` }}
                    aria-hidden
                  />
                ) : null}
              </Button>
            );
          })}
        </div>

        {!hideManualSlideControls ? (
          <div className='flex flex-wrap items-center justify-between gap-2 text-muted-foreground'>
            <Button
              type='button'
              variant='secondary'
              size='sm'
              className='gap-1 border-border bg-card text-foreground hover:bg-accent/60'
              onClick={goPrev}
              aria-label='Previous slide'
            >
              <ChevronLeftIcon className='size-4' aria-hidden />
              <span className='hidden sm:inline'>Previous</span>
            </Button>
            <span aria-live='polite' className='text-sm text-muted-foreground'>
              Slide {slideIndex + 1} of {SLIDE_COUNT}
            </span>
            <Button
              type='button'
              variant='secondary'
              size='sm'
              className='gap-1 border-border bg-card text-foreground hover:bg-accent/60'
              onClick={goNext}
              aria-label='Next slide'
            >
              <span className='hidden sm:inline'>Next</span>
              <ChevronRightIcon className='size-4' aria-hidden />
            </Button>
          </div>
        ) : null}
      </div>

      <div
        ref={bodyScrollRef}
        className='min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-6 sm:px-6'
      >
        <div className='mx-auto max-w-6xl'>
          <div
            className={cn(slideIndex !== 0 && 'hidden')}
            aria-hidden={slideIndex !== 0}
          >
            <MatchShareTopThreeSlide
              top={topThree}
              emailShareDisabled={emailShareDisabled}
              palette={sharePalette}
              {...shareHandlers}
            />
          </div>
          <div
            className={cn(slideIndex !== 1 && 'hidden')}
            aria-hidden={slideIndex !== 1}
          >
            <MatchShareQuizResultsListSlide
              sortedMatches={sortedMatches}
              emailShareDisabled={emailShareDisabled}
              palette={sharePalette}
              {...shareHandlers}
            />
          </div>
        </div>
      </div>

    </div>
  );

  return (
    <>
      {createPortal(overlay, document.body)}
      <MatchShareEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        matchResultsId={shareMatchResultsId?.trim() ?? null}
        shelterThemeVars={portalShelterThemeVars}
      />
    </>
  );
}
